import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment-timezone";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import Pagination from "../../../assets/SharedComponents/Pagination";
import NoRecord from "../../../assets/SharedComponents/Table/NoRecord";
import RecordCount from "../../../assets/SharedComponents/RecordCount/RecordCount";
import StaticFilter from "../../../assets/SharedComponents/Filters/StaticFilter";
import { BrokerListWrapper } from "../style/Style";
import API from "../../../api/axios";
import { APP_URLS } from "../../../api/url";
import { APIError } from "../../../core/utils";

const leadVerticalList = [
  { name: "All", value: "all" },
  { name: "Sell my car (Direct Offer)", value: "1" },
  { name: "Sell my car (Brokerage)", value: "2" },
  { name: "Buy a car", value: "3" },
  { name: "Car insurance", value: "4" },
  { name: "Car Financing", value: "5" },
];

const typeToVerticalMap = {
  sell_direct: "1",
  sell_brokerage: "2",
  buy: "3",
  insurance: "4",
  finance: "5",
};

const currentYear = new Date().getFullYear();
const currentMonth = String(new Date().getMonth() + 1);
const monthList = [
  { name: "January", value: "1" },
  { name: "February", value: "2" },
  { name: "March", value: "3" },
  { name: "April", value: "4" },
  { name: "May", value: "5" },
  { name: "June", value: "6" },
  { name: "July", value: "7" },
  { name: "August", value: "8" },
  { name: "September", value: "9" },
  { name: "October", value: "10" },
  { name: "November", value: "11" },
  { name: "December", value: "12" },
];
const yearList = Array.from({ length: 6 }, (_, i) => ({
  name: String(currentYear - i),
  value: String(currentYear - i),
}));

const formatJsonForCell = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  try {
    if (typeof value === "string") return value;
    return JSON.stringify(value, null, 2);
  } catch (e) {
    return String(value);
  }
};

const UnsoldLeads = () => {
  const search = useLocation().search;
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(search), [search]);
  const [, setSearchParams] = useSearchParams();

  const initialType = query.get("type");
  const initialLeadVertical = typeToVerticalMap[initialType] || "";
  const initialCount = query.get("per_page") || "10";
  const initialPage = query.get("page") ? parseInt(query.get("page"), 10) : 1;
  const initialMonth = query.get("month") || currentMonth;
  const initialYear = query.get("year") || String(currentYear);

  const [leadVertical, setLeadVertical] = useState(initialLeadVertical);
  const [countValue, setCountValue] = useState(initialCount);
  const [page, setPage] = useState(initialPage);
  const [selectedMonth, setSelectedMonth] = useState(initialMonth);
  const [selectedYear, setSelectedYear] = useState(initialYear);
  const [loaded, setLoaded] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPage, setTotalPage] = useState(0);

  const [reasonsModalOpen, setReasonsModalOpen] = useState(false);
  const [reasonsLoading, setReasonsLoading] = useState(false);
  const [reasons, setReasons] = useState([]);

  useEffect(() => {
    const nextParams = new URLSearchParams(query);

    if (leadVertical) {
      nextParams.set("lead_vertical", leadVertical);
    } else {
      nextParams.delete("lead_vertical");
    }

    if (countValue) {
      nextParams.set("per_page", countValue);
    } else {
      nextParams.delete("per_page");
    }

    if (page > 1) {
      nextParams.set("page", `${page}`);
    } else {
      nextParams.delete("page");
    }

    if (selectedMonth) {
      nextParams.set("month", selectedMonth);
    } else {
      nextParams.delete("month");
    }

    if (selectedYear) {
      nextParams.set("year", selectedYear);
    } else {
      nextParams.delete("year");
    }

    setSearchParams(nextParams);
  }, [leadVertical, countValue, page, selectedMonth, selectedYear, query, setSearchParams]);

  useEffect(() => {
    const fetchUnsoldLeads = async () => {
      setLoaded(true);
      try {
        const perPage = Number(countValue) > 0 ? Number(countValue) : 10;
        const params = new URLSearchParams({
          page: `${page}`,
          per_page: `${perPage}`,
        });

        if (leadVertical) {
          params.set("lead_vertical", leadVertical);
        }
        if (selectedMonth) {
          params.set("month", selectedMonth);
        }
        if (selectedYear) {
          params.set("year", selectedYear);
        }

        const response = await API.get(`${APP_URLS.UNSOLD_LEADS}?${params.toString()}`);
        const resp = response.data;

        if (resp.success === true) {
          const payload = resp.data || {};
          const items = payload.items || payload.data || [];
          setRows(items);
          setTotal(payload.total || 0);
          setTotalPage(payload.last_page || 0);
        } else {
          toast.error(resp.message, { position: toast.POSITION.TOP_RIGHT });
        }
      } catch (error) {
        const errorMessage = error?.response ? APIError(error) : "Failed to fetch unsold leads.";
        toast.error(errorMessage, { position: toast.POSITION.TOP_RIGHT });
      } finally {
        setLoaded(false);
      }
    };

    fetchUnsoldLeads();
  }, [leadVertical, countValue, page, selectedMonth, selectedYear]);

  useEffect(() => {
    if (totalPage > 0 && page > totalPage) {
      setPage(totalPage);
    }
  }, [page, totalPage]);

  const handleLeadVerticalChange = (e) => {
    setLeadVertical(e.target.value);
    setPage(1);
  };

  const handleCountChange = (val) => {
    setCountValue(val || "10");
    setPage(1);
  };

  const openReasonsModal = async (leadDataId) => {
    if (!leadDataId) return;
    if (reasonsLoading) return;

    setReasonsModalOpen(true);
    setReasonsLoading(true);
    setReasons([]);

    try {
      const response = await API.get(
        `${APP_URLS.UNSOLD_LEADS}/${leadDataId}/reasons`
      );
      const resp = response.data;

      if (resp.success === true) {
        setReasons(resp.data?.reasons || []);
      } else {
        toast.error(resp.message, { position: toast.POSITION.TOP_RIGHT });
      }
    } catch (error) {
      const errorMessage = error?.response ? APIError(error) : error?.message || "Failed to fetch reasons";
      toast.error(errorMessage, { position: toast.POSITION.TOP_RIGHT });
    } finally {
      setReasonsLoading(false);
    }
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(e.target.value);
    setPage(1);
  };

  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
    setPage(1);
  };

  return (
    <BrokerListWrapper>
      <div className="auctionMainContainer">
        <div className="row mt-4 align-items-center">
          <div className="col-md-4 d-flex justify-content-start mt-2 mt-md-0">
            <div
              className="backContainer"
              onClick={() => navigate("/broker-management")}
            >
              <span className="backButton">x</span>
              <span className="px-3 backButton">|</span>
              <span className="backButton">Back</span>
            </div>
          </div>
          <div className="col-md-4">
            <h1 className="mb-0">UNSOLD LEADS</h1>
          </div>
        </div>

        <div className="d-flex justify-content-end mt-0">
          <div className="d-flex gap-2 mb-2 select-info justify-content-end">
            <StaticFilter
              placeholder="Lead vertical"
              data={leadVerticalList}
              handleChange={handleLeadVerticalChange}
              value={leadVertical}
            />
            <StaticFilter
              placeholder="Month"
              data={monthList}
              handleChange={handleMonthChange}
              value={selectedMonth}
            />
            <StaticFilter
              placeholder="Year"
              data={yearList}
              handleChange={handleYearChange}
              value={selectedYear}
            />
            <RecordCount
              onSearchClick={handleCountChange}
              id="pageCount"
              val={countValue}
              filters={false}
              _filters={() => null}
            />
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between info-wrap">
          <div className="justify-content-end mt-1 me-2">
            <span className="text-start">
              Total Records : <b>{total}</b>
            </span>
          </div>
        </div>

        <div className="tableContainer">
          <table className="auctionTable">
            <thead className="tableHead">
              <tr className="table-row">
                <th>ID</th>
                <th>User</th>
                <th>Created Date</th>
                <th>Attempt Count</th>
              </tr>
            </thead>

            {loaded ? (
              <div className="d-flex justify-content-center three-dot">
                <ThreeDots
                  height="150"
                  width="150"
                  radius="9"
                  color="#637df4"
                  ariaLabel="three-dots-loading"
                  visible={true}
                />
              </div>
            ) : (
              <tbody>
                {rows.length > 0 ? (
                  rows.map((item) => (
                    <tr className="table-row" key={item.lead_data_id || item.id}>
                      <td>{item.lead_data_id || "-"}</td>
                      <td>
                        <NavLink
                          to={`/detail-member/${item.user_id}?tab=leads`}
                          className="navigationLink"
                        >
                          {item.user_name || item.user?.name || item.user || "-"}
                        </NavLink>
                      </td>
                      <td>
                        {item.lead_data_created_at || item.date || item.created_at
                          ? moment(item.lead_data_created_at || item.date || item.created_at)
                              .tz("Europe/Amsterdam")
                              .format("DD-MM-YYYY HH:mm")
                          : "-"}
                      </td>
                      <td>
                        <button 
                          className="btn attempt-button"  
                          onClick={() => openReasonsModal(item.lead_data_id)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              openReasonsModal(item.lead_data_id);
                            }
                          }}>
                            View Attempts ({item.attempt_count ?? "-"})
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4">
                      <NoRecord />
                    </td>
                  </tr>
                )}
              </tbody>
            )}
          </table>
        </div>

        {reasonsModalOpen && (
          <div className="modal fade show custom-modal modal-backdrop">
            <div className="modal">
              <div
                className="modal-dialog modal-dialog-centered modal-xl"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Reasons</h5>
                  </div>

                  <div className="modal-body">
                    {reasonsLoading ? (
                      <div className="d-flex justify-content-center three-dot">
                        <ThreeDots
                          height="60"
                          width="60"
                          radius="9"
                          color="#637df4"
                          ariaLabel="three-dots-loading"
                          visible={true}
                        />
                      </div>
                    ) : (
                      <div className="tableContainer reasonsTableContainer">
                        <table className="auctionTable reasonsTable">
                          <thead className="tableHead">
                            <tr className="table-row">
                              <th>Broker Name</th>
                              <th>Rejection Date</th>
                              <th>Reason</th>
                              <th className="payloadCellContent">Request</th>
                              <th className="payloadCellContent">Response</th>
                            </tr>
                          </thead>
                          <tbody className="table-body">
                            {reasons.length > 0 ? (
                              reasons.map((r) => (
                                <tr
                                  className="table-row"
                                  key={r.id || `${r.reason_code}-${r.created_at}`}
                                >
                                  <td>{r.broker?.name || "-"}</td>
                                  <td>
                                    {moment(r.created_at)
                                      .tz("Europe/Amsterdam")
                                      .format("DD-MM-YYYY HH:mm") || "-"}
                                  </td>
                                  <td>{r.reason || "-"}</td>
                                  <td className="payloadCellContent">
                                    <pre className="payloadCell">
                                      {formatJsonForCell(r.request_payload)}
                                    </pre>
                                  </td>
                                  <td className="payloadCellContent">
                                    <pre className="payloadCell">
                                      {formatJsonForCell(r.response_payload)}
                                    </pre>
                                  </td>
                                </tr>
                              ))
                            ) : (
                                  <NoRecord />
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="d-flex justify-content-end p-3">
                    <button
                      type="button"
                      className="btn cancel-btn btn-border"
                      onClick={() => setReasonsModalOpen(false)}
                      disabled={reasonsLoading}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div>
          {totalPage > 1 ? (
            <Pagination
              totalPages={totalPage}
              onPageClick={(nextPage) => {
                setPage(nextPage + 1);
              }}
              page={page}
              key={totalPage}
            />
          ) : (
            ""
          )}
        </div>
      </div>
    </BrokerListWrapper>
  );
};

export default UnsoldLeads;
